// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar, Clock, Plus, Edit, Trash2, Copy, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';
import { formatTimeRange } from "@/lib/dates";

interface TimeSlot {
  id: string;
  tournament_id: string;
  court_id?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  created_at: string;
  event_courts?: {
    id: string;
    name: string;
    notes?: string | null;
  };
}

interface VenueCourt {
  id: string;
  name: string;
  notes?: string | null;
  event_id: string;
}

interface Tournament {
  id: string;
  name: string;
  event_id: string;
  events?: {
    venue_id?: string;
  };
}

interface TimeSlotManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
}

export const TimeSlotManagementDialog: React.FC<TimeSlotManagementDialogProps> = ({
  isOpen,
  onClose,
  tournament,
}) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [courts, setCourts] = useState<VenueCourt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingSlot, setEditingSlot] = useState<TimeSlot | null>(null);
  const [isBulkMode, setIsBulkMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    court_id: "",
    start_time: "",
    end_time: "",
    duration_minutes: 60,
  });

  // Bulk form state
  const [bulkData, setBulkData] = useState({
    court_id: "",
    start_date: "",
    start_time: "08:00",
    end_time: "18:00",
    duration_minutes: 60,
    break_minutes: 15,
  });

  useEffect(() => {
    if (isOpen) {
      fetchTimeSlots();
      fetchCourts();
    }
  }, [isOpen, tournament.id]);

  const fetchTimeSlots = async () => {
    try {
      const { data, error } = await supabase
        .from("tournament_time_slots")
        .select(`
          *,
          event_courts (id, name, notes)
        `)
        .eq("tournament_id", tournament.id)
        .order("start_time");

      if (error) throw error;
      setTimeSlots(data || []);
    } catch (error) {
      console.error("Error fetching time slots:", error);
      toast.error("Failed to load time slots");
    }
  };

  const fetchCourts = async () => {
    try {
      console.log("Fetching courts for tournament:", tournament.name, "event_id:", tournament.event_id);
      
      let eventId = tournament.event_id;
      
      if (!eventId && tournament.id) {
        const { data: tournamentData, error: tournamentError } = await supabase
          .from("tournaments")
          .select("event_id")
          .eq("id", tournament.id)
          .single();

        if (tournamentError) {
          console.error("Error fetching tournament:", tournamentError);
          throw tournamentError;
        }
        eventId = tournamentData?.event_id;
        console.log("Fetched event_id from tournament:", eventId);
      }

      if (eventId) {
        console.log("Fetching courts for event_id:", eventId);
        const { data, error } = await supabase
          .from("event_courts")
          .select("*")
          .eq("event_id", eventId)
          .eq("is_active", true)
          .order("sort_order");

        if (error) throw error;
        console.log("Found courts:", data?.length);
        setCourts(data || []);
      } else {
        console.warn("No event_id found for tournament");
        setCourts([]);
      }
    } catch (error) {
      console.error("Error fetching courts:", error);
      toast.error("Failed to load courts");
    }
  };

  const handleCreateTimeSlot = async () => {
    if (!formData.court_id || !formData.start_time || !formData.end_time) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const startTime = new Date(`${new Date().toDateString()} ${formData.start_time}`);
      const endTime = new Date(`${new Date().toDateString()} ${formData.end_time}`);

      const { error } = await supabase
        .from("tournament_time_slots")
        .insert({
          tournament_id: tournament.id,
          court_id: formData.court_id,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          duration_minutes: formData.duration_minutes,
        });

      if (error) throw error;

      toast.success("Time slot created successfully");
      setIsCreateMode(false);
      resetForm();
      fetchTimeSlots();
    } catch (error) {
      console.error("Error creating time slot:", error);
      toast.error("Failed to create time slot");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkData.court_id || !bulkData.start_date || !bulkData.start_time || !bulkData.end_time) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const slots = [];
      const startDate = parseLocalDate(bulkData.start_date);
      const startTime = bulkData.start_time.split(':');
      const endTime = bulkData.end_time.split(':');
      
      let currentTime = new Date(startDate);
      currentTime.setHours(parseInt(startTime[0]), parseInt(startTime[1]), 0, 0);
      
      const endDateTime = new Date(startDate);
      endDateTime.setHours(parseInt(endTime[0]), parseInt(endTime[1]), 0, 0);

      while (currentTime < endDateTime) {
        const slotEnd = new Date(currentTime.getTime() + bulkData.duration_minutes * 60000);
        
        if (slotEnd <= endDateTime) {
          slots.push({
            tournament_id: tournament.id,
            court_id: bulkData.court_id,
            start_time: currentTime.toISOString(),
            end_time: slotEnd.toISOString(),
            duration_minutes: bulkData.duration_minutes,
          });
        }
        
        currentTime = new Date(slotEnd.getTime() + bulkData.break_minutes * 60000);
      }

      const { error } = await supabase
        .from("tournament_time_slots")
        .insert(slots);

      if (error) throw error;

      toast.success(`Created ${slots.length} time slots successfully`);
      setIsBulkMode(false);
      resetBulkForm();
      fetchTimeSlots();
    } catch (error) {
      console.error("Error creating bulk time slots:", error);
      toast.error("Failed to create time slots");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTimeSlot = async () => {
    if (!editingSlot || !formData.start_time || !formData.end_time) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const startTime = new Date(`${new Date().toDateString()} ${formData.start_time}`);
      const endTime = new Date(`${new Date().toDateString()} ${formData.end_time}`);

      const { error } = await supabase
        .from("tournament_time_slots")
        .update({
          court_id: formData.court_id,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          duration_minutes: formData.duration_minutes,
        })
        .eq("id", editingSlot.id);

      if (error) throw error;

      toast.success("Time slot updated successfully");
      setEditingSlot(null);
      resetForm();
      fetchTimeSlots();
    } catch (error) {
      console.error("Error updating time slot:", error);
      toast.error("Failed to update time slot");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTimeSlot = async (slotId: string) => {
    if (!confirm("Are you sure you want to delete this time slot?")) return;

    try {
      const { error } = await supabase
        .from("tournament_time_slots")
        .delete()
        .eq("id", slotId);

      if (error) throw error;

      toast.success("Time slot deleted successfully");
      fetchTimeSlots();
    } catch (error) {
      console.error("Error deleting time slot:", error);
      toast.error("Failed to delete time slot");
    }
  };

  const handleCopyTimeSlotsToAllCourts = async (sourceSlotId: string) => {
    const sourceSlot = timeSlots.find(slot => slot.id === sourceSlotId);
    if (!sourceSlot) return;

    if (!confirm("Copy this time slot to all other courts?")) return;

    setIsLoading(true);
    try {
      const otherCourts = courts.filter(court => court.id !== sourceSlot.court_id);
      const newSlots = otherCourts.map(court => ({
        tournament_id: tournament.id,
        court_id: court.id,
        start_time: sourceSlot.start_time,
        end_time: sourceSlot.end_time,
        duration_minutes: sourceSlot.duration_minutes,
      }));

      const { error } = await supabase
        .from("tournament_time_slots")
        .insert(newSlots);

      if (error) throw error;

      toast.success(`Copied time slot to ${otherCourts.length} courts`);
      fetchTimeSlots();
    } catch (error) {
      console.error("Error copying time slots:", error);
      toast.error("Failed to copy time slots");
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (slot: TimeSlot) => {
    setEditingSlot(slot);
    setFormData({
      court_id: slot.court_id || "",
      start_time: format(new Date(slot.start_time), "HH:mm"),
      end_time: format(new Date(slot.end_time), "HH:mm"),
      duration_minutes: slot.duration_minutes,
    });
    setIsCreateMode(true);
  };

  const resetForm = () => {
    setFormData({
      court_id: "",
      start_time: "",
      end_time: "",
      duration_minutes: 60,
    });
  };

  const resetBulkForm = () => {
    setBulkData({
      court_id: "",
      start_date: "",
      start_time: "08:00",
      end_time: "18:00",
      duration_minutes: 60,
      break_minutes: 15,
    });
  };

  const formatTimeSlot = (startTime: string, endTime: string) => {
    return formatTimeRange(startTime, endTime);
  };

  const groupSlotsByCourt = () => {
    const grouped = timeSlots.reduce((acc, slot) => {
      const courtId = slot.court_id || "unassigned";
      if (!acc[courtId]) {
        acc[courtId] = [];
      }
      acc[courtId].push(slot);
      return acc;
    }, {} as Record<string, TimeSlot[]>);

    return grouped;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Time Slot Management - {tournament.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* No Courts Warning */}
          {courts.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <MapPin className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">No courts available</p>
                <p className="text-sm text-amber-700 mt-1">
                  This event's venue doesn't have any courts configured. Please go to{" "}
                  <strong>Venues</strong> in the admin panel and add courts to the venue first.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setIsCreateMode(true)}
              className="flex items-center gap-2"
              disabled={courts.length === 0}
            >
              <Plus className="h-4 w-4" />
              Add Time Slot
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsBulkMode(true)}
              className="flex items-center gap-2"
              disabled={courts.length === 0}
            >
              <Calendar className="h-4 w-4" />
              Bulk Create
            </Button>
          </div>

          {/* Create/Edit Form */}
          {(isCreateMode || editingSlot) && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {editingSlot ? "Edit Time Slot" : "Create Time Slot"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="court">Court</Label>
                    <Select
                      value={formData.court_id}
                      onValueChange={(value) => setFormData({ ...formData, court_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select court" />
                      </SelectTrigger>
                      <SelectContent>
                        {courts.map((court) => (
                          <SelectItem key={court.id} value={court.id}>
                            {court.name}{court.notes ? ` (${court.notes})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="start_time">Start Time</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_time">End Time</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration">Duration (minutes)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingSlot ? handleEditTimeSlot : handleCreateTimeSlot}
                    disabled={isLoading}
                  >
                    {editingSlot ? "Update" : "Create"} Time Slot
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCreateMode(false);
                      setEditingSlot(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bulk Create Form */}
          {isBulkMode && (
            <Card>
              <CardHeader>
                <CardTitle>Bulk Create Time Slots</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div>
                    <Label htmlFor="bulk_court">Court</Label>
                    <Select
                      value={bulkData.court_id}
                      onValueChange={(value) => setBulkData({ ...bulkData, court_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select court" />
                      </SelectTrigger>
                      <SelectContent>
                        {courts.map((court) => (
                          <SelectItem key={court.id} value={court.id}>
                            {court.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="bulk_date">Date</Label>
                    <Input
                      id="bulk_date"
                      type="date"
                      value={bulkData.start_date}
                      onChange={(e) => setBulkData({ ...bulkData, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bulk_start">Start Time</Label>
                    <Input
                      id="bulk_start"
                      type="time"
                      value={bulkData.start_time}
                      onChange={(e) => setBulkData({ ...bulkData, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bulk_end">End Time</Label>
                    <Input
                      id="bulk_end"
                      type="time"
                      value={bulkData.end_time}
                      onChange={(e) => setBulkData({ ...bulkData, end_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bulk_duration">Duration (min)</Label>
                    <Input
                      id="bulk_duration"
                      type="number"
                      value={bulkData.duration_minutes}
                      onChange={(e) => setBulkData({ ...bulkData, duration_minutes: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bulk_break">Break (min)</Label>
                    <Input
                      id="bulk_break"
                      type="number"
                      value={bulkData.break_minutes}
                      onChange={(e) => setBulkData({ ...bulkData, break_minutes: parseInt(e.target.value) || 15 })}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleBulkCreate} disabled={isLoading}>
                    Create Time Slots
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsBulkMode(false);
                      resetBulkForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Time Slots Grid by Court */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Time Slots by Court</h3>
            {Object.entries(groupSlotsByCourt()).map(([courtId, slots]) => {
              const court = courts.find(c => c.id === courtId);
              return (
                <Card key={courtId}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {court ? `${court.name}${court.notes ? ` (${court.notes})` : ""}` : "Unassigned Court"}
                      <Badge variant="outline">{slots.length} slots</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {slots.map((slot) => (
                          <TableRow key={slot.id}>
                            <TableCell className="font-medium">
                              {formatTimeSlot(slot.start_time, slot.end_time)}
                            </TableCell>
                            <TableCell>{slot.duration_minutes} min</TableCell>
                            <TableCell>
                              {format(new Date(slot.created_at), "MMM d, yyyy")}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => startEdit(slot)}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCopyTimeSlotsToAllCourts(slot.id)}
                                  title="Copy to all courts"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteTimeSlot(slot.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {slots.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground">
                        No time slots for this court
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {timeSlots.length === 0 && (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No time slots yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create time slots to schedule matches for this tournament
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};